/**
 * Subscription related utility functions
 * @module utils/subscription
 */

import { Dayjs } from "dayjs";
import { getTimezoneDateWithOffset } from "../hooks/useCompanyLocalDate";

/**
 * Calculate monthly price from annual price
 * @param annualPrice - Annual subscription price
 * @returns Monthly equivalent price (not rounded to preserve accuracy)
 */
export const calculateMonthlyFromAnnual = (annualPrice: number): number => {
  if (annualPrice <= 0) return 0;
  return annualPrice / 12; // 반올림 제거하여 정확한 계산
};

/**
 * Calculate discount rate for annual subscription
 * @param monthlyPrice - Regular monthly price
 * @param annualPrice - Annual subscription price
 * @returns Discount percentage (0-100)
 */
export const calculateAnnualDiscountRate = (
  monthlyPrice: number,
  annualPrice: number,
): number => {
  // Validation: both prices must be positive
  if (monthlyPrice <= 0 || annualPrice <= 0) {
    return 0;
  }

  // Calculate monthly equivalent from annual price
  const monthlyFromAnnual = calculateMonthlyFromAnnual(annualPrice);

  // Prevent negative or invalid discount rates
  if (monthlyFromAnnual >= monthlyPrice) {
    return 0;
  }

  // Calculate discount percentage
  const discountRate = Math.round(
    ((monthlyPrice - monthlyFromAnnual) / monthlyPrice) * 100,
  );

  // Ensure discount is within valid range (0-100)
  return Math.max(0, Math.min(100, discountRate));
};

/**
 * Check if a subscription plan has valid annual pricing
 * @param annualPrice - Annual subscription price
 * @returns Whether annual pricing is available and valid
 */
export const hasValidAnnualPricing = (annualPrice: number): boolean => {
  return annualPrice > 0;
};

/**
 * Format subscription price for display
 * @param price - Price to format
 * @param currency - Currency code (default: USD)
 * @returns Formatted price string
 */
export const formatSubscriptionPrice = (
  price: number,
  currency: string = "USD",
): string => {
  return `${price.toLocaleString()} ${currency}`;
};

/**
 * Parse amount from string or number value
 * Handles currency symbols and formatting
 * @param value - Amount value to parse (can be string with currency symbols or number)
 * @returns Parsed number or NaN if invalid
 */
export const parseAmount = (value?: string | number): number => {
  if (value === undefined || value === null) {
    return Number.NaN;
  }

  if (typeof value === "number") {
    return Number.isNaN(value) ? Number.NaN : value;
  }

  // Remove all non-numeric characters except decimal point and minus sign
  const sanitized = value.replace(/[^0-9.-]/g, "");
  if (!sanitized) {
    return 0;  // Return 0 for empty string to avoid NaN in UI
  }

  const parsed = Number(sanitized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Check if the amount is free (0 or equivalent)
 * @param value - Amount value to check
 * @returns true if the amount is 0, false otherwise
 */
export const isFreeAmount = (value?: string | number): boolean => {
  // Invalid input should not be treated as free
  if (value === undefined || value === null) return false;

  // Empty string or currency symbols only should not be free
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return false;
    // Check if contains at least one digit
    if (!/\d/.test(trimmed)) return false;
  }

  return parseAmount(value) === 0;
};

/**
 * Calculate plan change date (subscription end date + 1 day)
 * @param subscriptionEndDate - The subscription end date string
 * @param gmtOffset - GMT offset for timezone calculation
 * @returns Dayjs object representing the plan change date
 */
export const calculatePlanChangeDate = (
  subscriptionEndDate: string,
  gmtOffset: number,
): Dayjs => {
  return getTimezoneDateWithOffset({
    date: subscriptionEndDate,
    gmtOffset,
  }).add(1, "day");
};